import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Lock } from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import { api, apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("This reset link is missing its token.");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      localStorage.setItem("bp_token", data.token);
      setDone(true);
      // Full page load so AuthContext refresh() runs and picks the new token cleanly.
      setTimeout(() => window.location.assign("/app"), 900);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F1E9] flex items-center justify-center p-6">
      <div className="w-full max-w-[400px]">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10"><BrandMark size={30} /><span className="font-display font-extrabold text-[19px]">BackPocket</span></Link>
        <div className="rounded-3xl bg-white border border-[#E8E5DE] p-8 shadow-[0_24px_60px_-40px_rgba(11,13,16,0.4)]" data-testid="reset-password-card">
          {done ? (
            <div className="text-center py-6">
              <div className="h-14 w-14 rounded-2xl bg-[#C8FF4D] grid place-items-center mx-auto mb-5"><Check size={26} className="text-[#0B0D10]" strokeWidth={2.5} /></div>
              <h1 className="font-display font-extrabold text-[1.6rem] tracking-[-0.01em]">Password updated</h1>
              <p className="text-[#3a3d44]/75 mt-2 text-[14px]">Taking you to your dashboard…</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              <div className="h-12 w-12 rounded-xl bg-[#0B0D10] grid place-items-center mb-5"><Lock size={20} className="text-[#C8FF4D]" strokeWidth={2} /></div>
              <h1 className="font-display font-extrabold text-[1.8rem] tracking-[-0.01em]">Choose a new password</h1>
              <p className="text-[#3a3d44]/80 mt-2 mb-6 text-[14.5px]">At least 6 characters. You'll be logged in right after.</p>
              <div className="mb-4"><Label className="text-[13px]">New password</Label><Input data-testid="reset-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} className="mt-1.5 h-11 bg-[#F4F1E9] rounded-xl" placeholder="••••••••" /></div>
              <div className="mb-6"><Label className="text-[13px]">Confirm new password</Label><Input data-testid="reset-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={6} className="mt-1.5 h-11 bg-[#F4F1E9] rounded-xl" placeholder="••••••••" /></div>
              <button data-testid="reset-submit" disabled={busy} className="btn-lift w-full bg-[#C8FF4D] text-[#0B0D10] rounded-full h-12 font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? "Updating…" : <>Update password <ArrowRight size={16} /></>}
              </button>
              <p className="text-[13px] text-center mt-6"><Link to="/login" className="text-[#3a3d44]/70 hover:text-[#0B0D10]">Back to login</Link></p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
